import { useEffect, useState } from 'react';
import { r, s } from '@/theme/tokens';
import { Panel, Stack, Text } from '@/ui';
import {
  settingsCopyTextSx,
  settingsPanelSurfaceSx,
} from '@/ui/composites/settingsPageTypographySx';
import { Accordion, AccordionDetails, AccordionSummary, Fade, Tooltip } from '@/ui/primitives';
import { Box, ExpandIcon } from '@/ui/primitives';
import type { ReactElement, ReactNode } from 'react';

type ProjectSettingsAccordionProps = {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  defaultExpanded?: boolean;
  preloadContent?: boolean;
  transitionMode?: 'auto' | 'fade' | 'none';
  disabled?: boolean;
  disabledReason?: string;
  forceExpanded?: boolean;
};

export const ProjectSettingsAccordion = ({
  title,
  description,
  children,
  defaultExpanded = false,
  preloadContent = false,
  transitionMode = 'auto',
  disabled = false,
  disabledReason,
  forceExpanded = false,
}: ProjectSettingsAccordionProps): ReactElement => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [hasExpanded, setHasExpanded] = useState(defaultExpanded);

  useEffect(() => {
    if (!disabled) return;
    setExpanded(false);
  }, [disabled]);

  useEffect(() => {
    if (disabled || !forceExpanded) return;
    setExpanded(true);
    setHasExpanded(true);
  }, [disabled, forceExpanded]);

  useEffect(() => {
    if (!expanded) return;
    setHasExpanded(true);
  }, [expanded]);

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      expanded={expanded}
      slots={transitionMode === 'fade' ? { transition: Fade } : undefined}
      slotProps={{
        transition: {
          mountOnEnter: !preloadContent,
          unmountOnExit: !preloadContent,
          ...(transitionMode === 'none'
            ? { timeout: 0 }
            : transitionMode === 'fade'
              ? { timeout: { enter: 180, exit: 120 } }
              : {}),
          ...(transitionMode === 'none'
            ? {}
            : transitionMode === 'fade'
              ? {}
              : {
                  easing: {
                    enter: 'cubic-bezier(0.2, 0, 0, 1)',
                    exit: 'cubic-bezier(0.4, 0, 1, 1)',
                  },
                }),
        },
      }}
      onChange={(event, nextExpanded) => {
        if (disabled) {
          return;
        }
        const target = event.target as HTMLElement | null;
        if (!target?.closest('.MuiAccordionSummary-expandIconWrapper')) {
          return;
        }
        setExpanded(nextExpanded);
      }}
      sx={[
        settingsPanelSurfaceSx,
        {
          border: (theme) => `1px solid ${theme.app.colors.border}`,
          borderRadius: r('card'),
          overflow: 'hidden',
          padding: s('xl'),
          '&::before': { display: 'none' },
        },
      ]}
    >
      <AccordionSummary
        expandIcon={
          <Tooltip
            title={disabled ? (disabledReason ?? 'Enable metrics to configure thresholds.') : ''}
            disableHoverListener={!disabled}
            arrow
          >
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <ExpandIcon fontSize="medium" sx={{ fontSize: 28 }} />
            </Box>
          </Tooltip>
        }
        sx={{
          padding: s('none'),
          minHeight: 'auto',
          alignItems: 'flex-start',
          cursor: 'default !important',
          userSelect: 'text',
          WebkitUserSelect: 'text',
          '&.MuiButtonBase-root': {
            cursor: 'default !important',
            userSelect: 'text',
            WebkitUserSelect: 'text',
          },
          '& .MuiAccordionSummary-content': {
            margin: s('none'),
            alignItems: 'flex-start',
            cursor: 'default !important',
            userSelect: 'text',
            WebkitUserSelect: 'text',
          },
          '& .MuiAccordionSummary-content .MuiTypography-root': {
            userSelect: 'text',
            WebkitUserSelect: 'text',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            alignSelf: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            pointerEvents: 'auto',
            opacity: disabled ? 0.45 : 1,
          },
        }}
      >
        <Stack gap="xs" sx={{ width: '100%' }}>
          <Text role="sectionTitle" color={disabled ? 'textSecondary' : undefined}>
            {title}
          </Text>
          {description ? (
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              {description}
            </Text>
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: s('none'),
          paddingTop: s('lg'),
        }}
      >
        {preloadContent || expanded || hasExpanded ? (
          <Panel container="none" padding="xl">
            {children}
          </Panel>
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
};
